import { DATE_FORMAT_ISO, format, parse } from '@eneco/common/date';
import { capitalizeFirstLetter } from '@eneco/common/string';
import useDC from '@eneco/dc/useDC';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { useGTM } from '@eneco/tracking';
import {
  DC_Domain_Models_Products_ProductType,
  Products_Offers_V3_OfferResponseModel,
  Products_Orders_SwitchTypeResponse,
} from '@monorepo-types/dc';
import { useLayoutData } from '@sitecore/common';

import { FlowContextSme } from '../context';
import { addLeadingZero, getProductCategoryFromProductType, getUpsellProductPrice } from '../helpers';
import { Offer, Product } from '../types';

interface EcommerceEventItem {
  item_name: string; // product name
  item_id: string; // product code (back in a day called SKU)
  product_combination?: string; // product combination code (HP8xP0xA12xWRVxWTWKTWxWWSET)
  price: number; // original monthly amount / one-time price
  price_upsell_gas?: number; // upsell product gas specific price
  price_upsell_electricity?: number; // upsell product electricity specific price
  item_brand: string; // brand, capitalized "DC_Repositories_Base_Enumerations_Label"
  item_category: 'consumenten' | 'zakelijk'; // market type
  item_category2: 'commodity' | 'non-commodity'; // product type category
  item_category3: DC_Domain_Models_Products_ProductType; // product type
  item_category4: string; // order type
  customer_segment: string; // order action code
  item_variant: string; // contract duration
  item_list_name: string; // funnel name
  index: number; // product position in the list (first product should have value 0)
  quantity: number; // quantity of the product, must be an integer
}

interface EcommerceEventSelectItem {
  event: 'select_item';
  ecommerce: {
    pre_selected: 'yes' | 'no';
    items: EcommerceEventItem[];
  };
}
interface EcommerceEventViewItem {
  event: 'view_item';
  ecommerce: {
    items: EcommerceEventItem[];
  };
}
interface EcommerceEventViewCart {
  event: 'view_cart';
  ecommerce: {
    value: number; // total order value (per month, tax included)
    currency: 'EUR';
    items: EcommerceEventItem[];
  };
}
interface EcommerceEventAddToCart {
  event: 'add_to_cart';
  ecommerce: {
    items: EcommerceEventItem[];
  };
}
interface EcommerceEventRemoveFromCart {
  event: 'remove_from_cart';
  ecommerce: {
    items: EcommerceEventItem[];
  };
}
interface EcommerceEventAddToWishlist {
  event: 'add_to_wishlist';
  ecommerce: {
    items: EcommerceEventItem[];
  };
}

interface EcommerceEventBeginCheckout {
  event: 'begin_checkout';
  ecommerce: {
    items: EcommerceEventItem[];
  };
}

interface EcommerceEventPurchase {
  event: 'purchase';
  contract_startdate: string; // start date of the contract, format: "DD-MM-YYYY"
  internalcampaign_id: string; // internal campaign id
  internalcampaign_name: string; // internal campaign name
  phonenumber: string;
  email: string;
  optinc1: boolean;
  optinc2: boolean;
  optinc3: boolean;
  ecommerce: {
    transaction_id: string; // unique identifier for the order / transaction
    currency: 'EUR';
    coupon?: string;
    value: number; // total order value (per month, tax included)
    tax: number; // tax included in the total order value
    items: EcommerceEventItem[];
  };
}

interface EventFillUsage {
  event: 'fill_usage';
  usage_filltype: 'manual' | 'estimated';
  usage_electricitysinglerate?: string;
  usage_electricitypeak?: string;
  usage_electricityoffpeak?: string;
  usage_gas?: string;
  usage_heating?: string;
  usage_hottapwater?: string;
  usage_redelivery?: string;
  component_type?: string;
}

type Event =
  | EcommerceEventViewItem
  | EcommerceEventSelectItem
  | EcommerceEventViewCart
  | EcommerceEventAddToCart
  | EcommerceEventRemoveFromCart
  | EcommerceEventAddToWishlist
  | EcommerceEventBeginCheckout
  | EcommerceEventPurchase
  | EventFillUsage;

export const useFlowTracking = () => {
  const { pushToDataLayer } = useGTM();
  const { label } = useDC();
  const { useFlowSelector } = useFlowHooks<FlowContextSme>();

  const flowContext = useFlowSelector(state => state.context);

  const { isSME, voucherCode, basketId, hasUpsellElectricity, hasUpsellGas } = flowContext;

  const {
    route: { fields = {} },
  } = useLayoutData();

  const pushEvent = (event: Event, isEcommerce?: boolean) => {
    // every Ecommerce event should be preceded by this
    if (isEcommerce) pushToDataLayer({ ecommerce: null });

    // JSON stringify && parse to filter out properties with undefined values
    pushToDataLayer(JSON.parse(JSON.stringify(event)));
  };

  const mapOfferProductsToEventItems = (
    selectedProducts: Product[],
    dcOffer?: Products_Offers_V3_OfferResponseModel, // contains more order related inforamtion such is discount / vouchers and etc
  ): EcommerceEventItem[] =>
    selectedProducts?.map((product, index) => {
      return {
        item_name: product.title ?? 'n/a',
        item_id: product.code,
        product_combination: product.productCombinationCode ?? 'n/a',
        item_brand: capitalizeFirstLetter(label),
        item_list_name: fields.flowName?.value?.toString() ?? 'n/a',
        price: Number(product.costsMonthly?.vatIncluded),
        discount: 0,
        item_category: isSME ? 'zakelijk' : 'consumenten',
        item_category2: getProductCategoryFromProductType(product.type),
        item_category3: product.type,
        item_category4: 'n/a',
        customer_segment: dcOffer?.configuration?.actionCode ?? 'n/a',
        item_variant: product.variant ?? 'n/a',
        index,
        quantity: 1,
      };
    });

  const roundUsageValueToRange = (value: number, rangeSize: number): string => {
    // 1 >>> 1 - 100
    // 1500 >>> 1401 - 1500
    // 1501 >>> 1501 - 1600

    const min = Math.floor((value - 1) / rangeSize) * (value <= rangeSize ? 0 : rangeSize) + 1;
    const max = min + rangeSize - 1;

    return `${min} - ${max}`;
  };

  const trackViewItem = (viewedOffer: Offer, dcOffer?: Products_Offers_V3_OfferResponseModel): void => {
    const event: EcommerceEventViewItem = {
      event: 'view_item',
      ecommerce: {
        items: mapOfferProductsToEventItems(viewedOffer.products, dcOffer),
      },
    };

    pushEvent(event, true);
  };

  const trackSelectItem = (viewedOffer: Offer, dcOffer?: Products_Offers_V3_OfferResponseModel, preSelected?: boolean): void => {
    const event: EcommerceEventSelectItem = {
      event: 'select_item',
      ecommerce: {
        pre_selected: preSelected ? 'yes' : 'no',
        items: mapOfferProductsToEventItems(viewedOffer.products, dcOffer),
      },
    };

    pushEvent(event, true);
  };

  const trackViewCart = (selectedOffer: Offer, dcOffer?: Products_Offers_V3_OfferResponseModel): void => {
    // TODO: uncomment it
    // temporary disabled "view_cart" event due to errors in the GA implementation
    // const event: EcommerceEventViewCart = {
    //   event: 'view_cart',
    //   ecommerce: {
    //     value: Number(selectedOffer.costsMonthly?.vatIncluded),
    //     currency: 'EUR',
    //     items: mapOfferProductsToEventItems(selectedOffer.products, dcOffer),
    //   },
    // };
    // pushEvent(event, true);
  };

  const trackRemoveFromCart = (selectedOffer: Offer, dcOffer?: Products_Offers_V3_OfferResponseModel): void => {
    const event: EcommerceEventRemoveFromCart = {
      event: 'remove_from_cart',
      ecommerce: {
        items: mapOfferProductsToEventItems(selectedOffer.products, dcOffer),
      },
    };

    pushEvent(event, true);
  };
  const trackAddToCart = (selectedOffer: Offer, dcOffer?: Products_Offers_V3_OfferResponseModel): void => {
    const event: EcommerceEventAddToCart = {
      event: 'add_to_cart',
      ecommerce: {
        items: mapOfferProductsToEventItems(selectedOffer.products, dcOffer),
      },
    };

    pushEvent(event, true);
  };

  const trackAddToWishList = (selectedOffer: Offer, dcOffer?: Products_Offers_V3_OfferResponseModel): void => {
    const event: EcommerceEventAddToWishlist = {
      event: 'add_to_wishlist',
      ecommerce: {
        items: mapOfferProductsToEventItems(selectedOffer.products, dcOffer),
      },
    };

    pushEvent(event, true);
  };

  const trackBeginCheckout = (selectedOffer: Offer, dcOffer?: Products_Offers_V3_OfferResponseModel): void => {
    const event: EcommerceEventBeginCheckout = {
      event: 'begin_checkout',
      ecommerce: {
        items: mapOfferProductsToEventItems(selectedOffer.products, dcOffer),
      },
    };

    pushEvent(event, true);
  };

  const trackPurchase = (
    selectedOffer: Offer,
    dcOffer?: Products_Offers_V3_OfferResponseModel,
    switchTypes?: Array<Products_Orders_SwitchTypeResponse> | null,
  ): void => {
    // TODO: fix deprecation warning, here we need a date formatter
    // that works outside of a react components (not a hook)
    const contractStartDate = format(parse(selectedOffer?.contractStartDate ?? '', DATE_FORMAT_ISO))?.split('-');

    const { C1, C2, C3, phoneNumber, emailAddress } = flowContext;

    const event: EcommerceEventPurchase = {
      event: 'purchase',
      contract_startdate: `${addLeadingZero(contractStartDate?.[0] ?? '')}-${addLeadingZero(
        contractStartDate?.[1] ?? '',
      )}-${contractStartDate?.[2]}`,
      internalcampaign_id: '',
      internalcampaign_name: '',
      phonenumber: phoneNumber ?? 'n/a',
      email: emailAddress ?? 'n/a',
      optinc1: C1 ?? false,
      optinc2: C2 ?? false,
      optinc3: C3 ?? false,
      ecommerce: {
        transaction_id: basketId ?? '',
        value: Number(selectedOffer.costsMonthly?.vatIncluded),
        tax: Number(selectedOffer.costsMonthly?.vatAmount),
        currency: 'EUR',
        coupon: voucherCode ?? '',
        items: mapOfferProductsToEventItems(selectedOffer.products, dcOffer).map(product => {
          // add product specific properties

          // upsell product gas
          if (hasUpsellGas && product.item_category3 === 'gas') {
            product.price_upsell_gas = Number(getUpsellProductPrice('gas', flowContext, dcOffer).toFixed(2));
          }

          // upsell product electricity
          if (hasUpsellElectricity && product.item_category3 === 'electricity') {
            product.price_upsell_electricity = Number(
              getUpsellProductPrice('electricity', flowContext, dcOffer).toFixed(2),
            );
          }

          product.item_category4 =
            switchTypes?.find(type => type.productType?.toLowerCase() === product.item_category3)?.orderType ?? '';

          return product;
        }),
      },
    };

    pushEvent(event, true);
  };

  const trackFillUsage = (flowContext: FlowContextSme): void => {
    const event: EventFillUsage = {
      event: 'fill_usage',
      usage_filltype: flowContext.isUsageKnown ? 'manual' : 'estimated',

      ...(flowContext.hasDoubleMeter
        ? {
            usage_electricitypeak: roundUsageValueToRange(flowContext.usageElectricityHigh ?? 0, 100),
            usage_electricityoffpeak: roundUsageValueToRange(flowContext.usageElectricityLow ?? 0, 100),
          }
        : {
            usage_electricitysinglerate: roundUsageValueToRange(flowContext.usageElectricityHigh ?? 0, 100),
          }),

      ...(flowContext.solarPanelsOutput && {
        usage_redelivery: roundUsageValueToRange(flowContext.solarPanelsOutput, 100),
      }),

      usage_gas: roundUsageValueToRange(flowContext.usageGas ?? 0, 100),

      // usage_heating && usage_hottapwater must have smaller
      // ranges due to smaller usage values
      usage_heating: roundUsageValueToRange(flowContext.usageWarmth ?? 0, 10),
      usage_hottapwater: roundUsageValueToRange(flowContext.usageWater ?? 0, 10),
      component_type: fields.flowName?.value?.toString() ?? '',
    };

    pushEvent(event);
  };

  return {
    trackViewItem,
    trackSelectItem,
    trackViewCart,
    trackAddToCart,
    trackRemoveFromCart,
    trackAddToWishList,
    trackBeginCheckout,
    trackPurchase,
    trackFillUsage,
  };
};
